import React, { useEffect, useRef, useState } from "react";
import {
  Archive,
  ChatText,
  Forward,
  Reply,
  ReplyAll,
  ThreeDots,
  Trash,
} from "react-bootstrap-icons";
import { useTranslation } from "react-i18next";
import styles from "./messages_card_mobile.module.css";

interface SwipeableMessageRowMobileProps {
  id: string;
  isOpen: boolean;
  onOpen: (id: string) => void;
  onClose: () => void;
  onPress: () => void;
  onReply?: () => void;
  onReplyAll?: () => void;
  onForward?: () => void;
  onComment?: () => void;
  onMove?: () => void;
  onDelete?: () => void;
  children: React.ReactNode;
}

const ACTION_WIDTH = 64;
const VISIBLE_ACTION_COUNT = 3;
const OPEN_THRESHOLD = 48;

const SwipeableMessageRowMobile: React.FC<SwipeableMessageRowMobileProps> = ({
  id,
  isOpen,
  onOpen,
  onClose,
  onPress,
  onReply,
  onReplyAll,
  onForward,
  onComment,
  onMove,
  onDelete,
  children,
}) => {
  const { t } = useTranslation();
  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);
  const touchDeltaX = useRef(0);
  const isHorizontalSwipe = useRef(false);
  const suppressClickRef = useRef(false);
  const [dragOffset, setDragOffset] = useState(0);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const actions = [
    onReply && {
      key: "reply",
      label: t("reply", { ns: "layout_components", defaultValue: "Reply" }),
      icon: <Reply size={18} />,
      className: styles.mobileSwipeActionReply,
      handler: onReply,
    },
    onReplyAll && {
      key: "replyAll",
      label: t("reply_all", { ns: "layout_components", defaultValue: "Reply All" }),
      icon: <ReplyAll size={18} />,
      className: styles.mobileSwipeActionReplyAll,
      handler: onReplyAll,
    },
    onForward && {
      key: "forward",
      label: t("forward", { ns: "layout_components", defaultValue: "Forward" }),
      icon: <Forward size={18} />,
      className: styles.mobileSwipeActionForward,
      handler: onForward,
    },
    onComment && {
      key: "comment",
      label: t("comment", { ns: "layout_components", defaultValue: "Comment" }),
      icon: <ChatText size={18} />,
      className: styles.mobileSwipeActionComment,
      handler: onComment,
    },
    onMove && {
      key: "move",
      label: t("move", { ns: "layout_components", defaultValue: "Move" }),
      icon: <Archive size={18} />,
      className: styles.mobileSwipeActionMove,
      handler: onMove,
    },
    onDelete && {
      key: "delete",
      label: t("delete", { ns: "layout_components", defaultValue: "Delete" }),
      icon: <Trash size={18} />,
      className: styles.mobileSwipeActionDelete,
      handler: onDelete,
    },
  ].filter(Boolean) as Array<{
    key: string;
    label: string;
    icon: React.ReactNode;
    className: string;
    handler: () => void;
  }>;
  const primaryAction =
    actions.find((action) => action.key === "reply") ??
    actions.find((action) => action.key === "comment") ??
    actions.find((action) => action.key === "move") ??
    actions.find((action) => action.key === "forward") ??
    null;
  const deleteAction = actions.find((action) => action.key === "delete") ?? null;
  const secondaryActions = actions.filter(
    (action) => action.key !== deleteAction?.key && action.key !== primaryAction?.key,
  );
  const visibleActions = [
    secondaryActions.length > 0 && {
      key: "more",
      label: t("more", { ns: "layout_components", defaultValue: "More" }),
      icon: <ThreeDots size={18} />,
      className: styles.mobileSwipeActionMore,
      handler: () => setShowMoreMenu((current) => !current),
    },
    primaryAction,
    deleteAction,
  ].filter(Boolean) as Array<{
    key: string;
    label: string;
    icon: React.ReactNode;
    className: string;
    handler: () => void;
  }>;
  const actionsWidth = VISIBLE_ACTION_COUNT * ACTION_WIDTH;

  useEffect(() => {
    setDragOffset(isOpen ? -actionsWidth : 0);
    if (!isOpen) {
      setShowMoreMenu(false);
    }
  }, [actionsWidth, isOpen]);

  const handleTouchStart = (event: React.TouchEvent<HTMLDivElement>) => {
    touchStartX.current = event.touches[0]?.clientX ?? null;
    touchStartY.current = event.touches[0]?.clientY ?? null;
    touchDeltaX.current = 0;
    isHorizontalSwipe.current = false;
    suppressClickRef.current = false;
  };

  const handleTouchMove = (event: React.TouchEvent<HTMLDivElement>) => {
    if (touchStartX.current == null) return;
    const currentX = event.touches[0]?.clientX ?? touchStartX.current;
    const currentY = event.touches[0]?.clientY ?? touchStartY.current ?? 0;
    const deltaX = currentX - touchStartX.current;
    const deltaY = currentY - (touchStartY.current ?? currentY);

    if (!isHorizontalSwipe.current) {
      if (Math.abs(deltaX) > 8 && Math.abs(deltaX) > Math.abs(deltaY)) {
        isHorizontalSwipe.current = true;
      } else if (Math.abs(deltaY) > 8) {
        return;
      }
    }

    if (!isHorizontalSwipe.current) return;

    event.preventDefault();
    suppressClickRef.current = true;
    touchDeltaX.current = deltaX;

    const nextOffset = isOpen
      ? Math.max(-actionsWidth, Math.min(0, -actionsWidth + deltaX))
      : Math.max(-actionsWidth, Math.min(0, deltaX));

    setDragOffset(nextOffset);
  };

  const handleTouchEnd = () => {
    if (touchStartX.current == null) return;

    if (touchDeltaX.current <= -OPEN_THRESHOLD) {
      onOpen(id);
    } else if (touchDeltaX.current >= OPEN_THRESHOLD) {
      onClose();
    } else {
      setDragOffset(isOpen ? -actionsWidth : 0);
    }

    touchStartX.current = null;
    touchStartY.current = null;
    touchDeltaX.current = 0;
    isHorizontalSwipe.current = false;
  };

  const handleClickCapture = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!suppressClickRef.current) return;

    event.preventDefault();
    event.stopPropagation();
    suppressClickRef.current = false;
  };

  const handleForegroundClick = () => {
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      return;
    }

    onPress();
  };

  const stopActionEvent = (
    event: React.MouseEvent<HTMLButtonElement> | React.TouchEvent<HTMLButtonElement>,
  ) => {
    event.preventDefault();
    event.stopPropagation();
  };

  return (
    <div className={styles.mobileSwipeRow}>
      <div
        className={styles.mobileSwipeActions}
        style={{ width: actionsWidth }}
      >
        {visibleActions.map((action) => (
          <button
            key={action.key}
            type="button"
            className={`${styles.mobileSwipeAction} ${action.className}`}
            onMouseDown={stopActionEvent}
            onTouchStart={stopActionEvent}
            onClick={(event) => {
              stopActionEvent(event);
              action.handler();
              if (action.key !== "more") {
                onClose();
              }
            }}
          >
            {action.icon}
            <span>{action.label}</span>
          </button>
        ))}
        {showMoreMenu && secondaryActions.length > 0 && (
          <div className={styles.mobileSwipeMoreMenu}>
            {secondaryActions.map((action) => (
              <button
                key={action.key}
                type="button"
                className={`${styles.mobileSwipeAction} ${action.className}`}
                onMouseDown={stopActionEvent}
                onTouchStart={stopActionEvent}
                onClick={(event) => {
                  stopActionEvent(event);
                  action.handler();
                  setShowMoreMenu(false);
                  onClose();
                }}
              >
                {action.icon}
                <span>{action.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <div
        className={styles.mobileSwipeForeground}
        style={{
          transform: `translateX(${dragOffset}px)`,
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
        onClickCapture={handleClickCapture}
        onClick={handleForegroundClick}
      >
        {children}
      </div>
    </div>
  );
};

export default SwipeableMessageRowMobile;
